import React, { useCallback } from 'react';

import {
  DescriptionList,
  DescriptionItem,
  Typography,
  Link,
  Tags,
} from '@gogame/components';
import { useQueryClient } from '@gogame/query';
import { useLinkBasenameWithRoot, useScreenState } from '@gogame/screens';

import ConceptFamilyDetails from 'scenes/Gallery/GalleryPage/ConceptFamilies/ConceptFamilyDetails';
import { useTranscriptions } from 'scenes/Gallery/GalleryPage/Concepts/data';
import * as CONCEPT from 'scenes/Gallery/GalleryPage/Concepts/view-constants';
import ExpirationDate from 'scenes/Gallery/components/ExpirationDate';
import Highlights from 'scenes/Gallery/components/Highlights';
import {
  CONCEPT_ID,
  SEARCH_BY_TRANSCRIPTION,
} from 'scenes/Gallery/GalleryPage/constants';
import { useGalleryPermissions } from 'scenes/Gallery/GalleryPage/hooks';
import {
  conceptPreviewLink,
  galleryLink,
} from 'scenes/Gallery/GalleryPage/navigation';
import { valueParserCommaListFromStrings } from 'scenes/Gallery/GalleryPage/utils/valueParsers';
import { DIMENSION_CONCEPT } from 'scenes/Trackers/Tracker/constants';
import TrackerAffiliateLink from 'scenes/Gallery/components/TrackerAffiliateLink';

const ConceptDetailsGeneral = ({
  data = {},
  withNameField = true,
  openChildComponent,
  termWidth = '25%',
  fieldAds,
}) => {
  const { filters, searchType, searchQuery } = useScreenState();

  const {
    isAllowConceptFamilies,
    isAllowUseInAffiliates,
    isAllowPromoCodes,
    isAllowExclusiveForAffiliates,
  } = useGalleryPermissions();

  const { [CONCEPT.ID]: conceptId, [CONCEPT.HAS_STATS]: hasStats } = data;

  const { data: transcriptions } = useTranscriptions({ id: conceptId });

  const queryClient = useQueryClient();

  const galleryBasename = useLinkBasenameWithRoot(galleryLink);

  const handleOpenInNewTab = useCallback(() => {
    const link = conceptPreviewLink.stringify(
      {
        [CONCEPT_ID]: `${conceptId}`,
        filters,
        creatives: {
          tableSettings: {
            orderDir: 'asc',
          },
        },
      },
      {
        basename: galleryBasename,
      }
    );
    window.open(link);
  }, [conceptId, galleryBasename]);

  const handleOpenConceptFamily = useCallback(
    (id) => {
      openChildComponent(ConceptFamilyDetails, {
        id,
        onChange: () => {
          queryClient.resetQueries({ active: true });
        },
      });
    },
    [openChildComponent, ConceptFamilyDetails, queryClient]
  );

  const handleOpenPromoCode = (id) => {
    import('scenes/PromoCodes/components/PromoCodeDetails').then(
      (PromoCodeDetails) => {
        openChildComponent(PromoCodeDetails.default, {
          id,
          onChange: () => {
            queryClient.resetQueries({ active: true });
          },
        });
      }
    );
  };

  return (
    <DescriptionList
      stripEmpty={true}
      data={data}
      disableGutters={true}
      termWidth={termWidth}
    >
      {withNameField ? (
        <DescriptionItem
          label={'Name'}
          field={CONCEPT.NAME}
          type={({ value }) => (
            <Link
              data-cy={'Concept Page'}
              onClick={handleOpenInNewTab}
              target={'_blank'}
            >
              <Typography>{value}</Typography>
            </Link>
          )}
        />
      ) : null}
      <DescriptionItem
        label={'Start date'}
        field={CONCEPT.START_DATE}
        type={'date'}
        template={'LL'}
      />
      <DescriptionItem
        label={'Expiration date'}
        field={CONCEPT.EXPIRATION_DATE}
        type={({ value }) => <ExpirationDate value={value} />}
        template={'LL'}
      />
      <DescriptionItem
        label={'Applications'}
        field={CONCEPT.APPLICATIONS}
        valueParser={valueParserCommaListFromStrings}
      />
      {isAllowConceptFamilies ? (
        <DescriptionItem
          label={'Concept families'}
          field={CONCEPT.CONCEPT_FAMILIES}
          type={'typographyList'}
          unitsLabel={'Concept families'}
          limit={1}
          listType={'link'}
          item={({ id, name }) => (
            <Link onClick={() => handleOpenConceptFamily(id)}>
              <Typography>{name}</Typography>
            </Link>
          )}
        />
      ) : null}
      {isAllowPromoCodes ? (
        <DescriptionItem
          label={'Promo code'}
          field={CONCEPT.PROMO_CODE}
          linkLabelField={'name'}
          type={'link'}
          onValueClick={(_e, { id }) => handleOpenPromoCode(id)}
        />
      ) : null}
      <DescriptionItem
        label={'Internal Tags'}
        field={CONCEPT.INTERNAL_TAGS}
        type={({ value }) => <Tags data={value} lineClamp={4} />}
      />
      <DescriptionItem label={'Description'} field={CONCEPT.DESCRIPTION} />
      {isAllowExclusiveForAffiliates ? (
        <DescriptionItem
          label={'Exclusive for'}
          field={CONCEPT.EXCLUSIVE_FOR_AFFILIATES}
          type={'typographyList'}
          unitsLabel={'Affiliates'}
          limit={3}
          listType={'link'}
          item={({ id, name }) => (
            <TrackerAffiliateLink
              id={id}
              dimension={DIMENSION_CONCEPT}
              drillDownId={conceptId}
            >
              <Typography>{name}</Typography>
            </TrackerAffiliateLink>
          )}
        />
      ) : null}
      {hasStats && isAllowUseInAffiliates ? (
        <DescriptionItem
          label={'Used in affiliates'}
          field={CONCEPT.USED_IN_AFFILIATES}
          type={'typographyList'}
          unitsLabel={'Affiliates'}
          limit={3}
          listType={'link'}
          item={({ id, name }) => (
            <TrackerAffiliateLink
              id={id}
              dimension={DIMENSION_CONCEPT}
              drillDownId={conceptId}
            >
              <Typography>{name}</Typography>
            </TrackerAffiliateLink>
          )}
        />
      ) : null}
      <DescriptionItem
        label={'Transcript'}
        collapsable={true}
        value={
          transcriptions ? (
            <Highlights
              text={transcriptions}
              highlight={
                searchType === SEARCH_BY_TRANSCRIPTION ? searchQuery : null
              }
            />
          ) : null
        }
      />
      {fieldAds}
    </DescriptionList>
  );
};

export default ConceptDetailsGeneral;
